import Icon from '@/components/icons/Icon';
import { classnames, roundRating } from '@/helpers';
import useMobileView from '@/hooks/useMobileView';
import { __ } from '@/locale';
import styles from './Stats.module.scss';

const getStars = (rating, ratingFilter, size) => {
  let empty = false;
  return [1, 2, 3, 4, 5].map((i, key) => {
    let fill = 100;
    if (i > rating) {
      fill = !empty ? (rating % 1) * 100 : 0;
      empty = true;
    }
    return (
      <div key={key} className="relative">
        <Icon variant="star-filled" color="black-50" style={{ width: size, height: size }} />
        <Icon
          className="absolute inset-0"
          variant="star-filled"
          color={ratingFilter === 0 || ratingFilter === rating ? 'warning-500' : 'black-200'}
          style={{ width: size, height: size, clipPath: `inset(0 ${100 - fill}% 0 0)` }}
        />
      </div>
    );
  });
};

function Stats(props) {
  const {
    stats: { score, count },
    ratingCounts,
    reviewCounts,
    ratingFilter = 0,
    onRatingClick,
    showScore = true,
  } = props;
  const { isMobileView } = useMobileView();
  if (!score || !count || !ratingCounts || count < 5) {
    return null;
  }

  const getStyle = (rating) => {
    if (ratingFilter === 0 || ratingFilter === rating) {
      return classnames(styles.active, 'hover:border-border_focus');
    } else {
      return classnames(styles.disabled, 'hover:border-border_focus');
    }
  };

  const handleRatingClick = (rating) => {
    onRatingClick(rating);
  };

  const spread = Object.keys(ratingCounts)
    .sort((a, b) => b - a)
    .map((key, i) => (
      <div
        key={i}
        className={classnames('p-3xs flex cursor-pointer items-center justify-end rounded-sm border-4', getStyle(key))}
        onClick={() => handleRatingClick(key)}>
        <span className="text-fg_secondary pr-sm">{key}</span>
        <div className="gap-xxs flex">{getStars(key, ratingFilter, 16)}</div>
        <div className={classnames(styles.pgbar, 'mx-3 h-[4px] flex-1 overflow-hidden rounded-sm md:min-w-[200px]')}>
          <div
            className={classnames(styles.pgbarval, 'h-[4px] rounded-l-sm')}
            role="progressbar"
            style={{ width: (ratingCounts[key] / count) * 100 + '%' }}
            aria-valuemin="0"
            aria-valuemax="100"></div>
        </div>
        <span className="text-fg_link text-link text-m w-[30px] text-right underline underline-offset-4 md:w-[50px]">
          ({ratingCounts[key] < 1000 ? ratingCounts[key] : '+999'})
        </span>
      </div>
    ));
  return (
    <div className={`gap-3 py-6 ${props.className ? props.className : ''}`}>
      {showScore && (
        <div className="flex items-center justify-between pl-2">
          <div className="gap-sm flex items-end">
            <span className="text-h-3xl font-bold">{roundRating(score, 1)}</span>
            <span className="text-h-l pb-xxs">{'av'}</span>
            <span className="text-h-3xl font-bold">{'5'}</span>
          </div>
          {!ratingFilter && !isMobileView && (
            <div className="gap-xxs flex">{getStars(roundRating(score, 1), ratingFilter, 24)}</div>
          )}
        </div>
      )}
      <div className="flex w-full flex-1 flex-col">{spread}</div>
      {Boolean(ratingFilter) && (
        <div className="mt-6 flex flex-col gap-1">
          <span className="font-semibold text-black">{__('ReviewsWithStarRating', { rating: ratingFilter })}</span>
          <span className="text-black-600 text-m">
            {__('reviewsFromRatingsCounts', {
              reviews: reviewCounts[ratingFilter],
              ratings: ratingCounts[ratingFilter],
            })}
          </span>
        </div>
      )}
    </div>
  );
}
export default Stats;
